import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { toast } from '@/utils/toast'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true }
    },
    {
      path: '/mobile/dingtalk/auth',
      name: 'DingTalkAuth',
      component: () => import('@/views/mobile/DingTalkAuthView.vue'),
      meta: { public: true }
    },
    {
      path: '/mobile/dingtalk/bind',
      name: 'DingTalkBind',
      component: () => import('@/views/mobile/DingTalkBindView.vue'),
      meta: { public: true }
    },
    {
      path: '/mobile/dingtalk/auto-bind',
      name: 'DingTalkAutoBind',
      component: () => import('@/views/mobile/MobileAutoBindView.vue'),
      meta: { public: true }
    },
    {
      path: '/mobile/work-order',
      name: 'MobileWorkOrder',
      component: () => import('@/views/mobile/MobileWorkOrderView.vue'),
      meta: { public: true }
    },
    {
      path: '/mobile',
      name: 'MobileLayout',
      component: () => import('@/views/mobile/MobileLayout.vue'),
      redirect: '/mobile/work-orders',
      children: [
        {
          path: 'work-orders',
          name: 'MobileWorkOrderList',
          component: () => import('@/views/mobile/MobileWorkOrderListView.vue'),
          meta: { public: false }
        },
        {
          path: 'work-order-stats',
          name: 'MobileWorkOrderStats',
          component: () => import('@/views/mobile/MobileWorkOrderStatsView.vue'),
          meta: { public: false }
        },
        {
          path: 'me',
          name: 'MobileMe',
          component: () => import('@/views/mobile/MobileMeView.vue'),
          meta: { public: false }
        }
      ]
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/BigScreenView.vue'),
      meta: { roles: [] }
    },
    {
      path: '/project/:id/dashboard',
      name: 'ProjectDashboard',
      component: () => import('@/views/ProjectBigScreenView.vue'),
      meta: { roles: [] }
    },
    {
      path: '/',
      name: 'Layout',
      component: () => import('@/views/LayoutView.vue'),
      redirect: '/dashboard',
      children: [
        {
          path: 'devices',
          name: 'Devices',
          component: () => import('@/views/DeviceView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'devices/:deviceId/remote-control',
          name: 'RemoteControl',
          component: () => import('@/views/RemoteControlView.vue'),
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
        },
        {
          path: 'video-linkage',
          name: 'VideoLinkage',
          component: () => import('@/views/VideoLinkageView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'components',
          name: 'Components',
          component: () => import('@/views/ComponentView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'alarms',
          name: 'Alarms',
          component: () => import('@/views/AlarmView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'work-orders',
          name: 'WorkOrders',
          component: () => import('@/views/WorkOrderView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'work-orders/pool',
          name: 'WorkOrderPool',
          component: () => import('@/views/WorkOrderPoolView.vue'),
          meta: { roles: [] }
        },
        {
          path: 'reports',
          name: 'Reports',
          component: () => import('@/views/report/ReportView.vue'),
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
        },
        {
          path: 'system',
          name: 'System',
          component: () => import('@/views/system/SystemLayout.vue'),
          redirect: '/system/users',
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] },
          children: [
            {
              path: 'users',
              name: 'SystemUsers',
              component: () => import('@/views/system/UserView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
            {
              path: 'roles',
              name: 'SystemRoles',
              component: () => import('@/views/system/RoleView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin'] }
            },
            {
              path: 'locations',
              name: 'SystemLocations',
              component: () => import('@/views/system/LocationView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
            {
              path: 'tenants',
              name: 'SystemTenants',
              component: () => import('@/views/system/TenantView.vue'),
              meta: { roles: ['platform_admin'] }
            },
            {
              path: 'password',
              name: 'SystemPassword',
              component: () => import('@/views/system/ChangePasswordView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
            {
              path: 'open-apps',
              name: 'SystemOpenApps',
              component: () => import('@/views/system/OpenAppView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
            {
              path: 'open-apps/:appId',
              name: 'SystemOpenAppDetail',
              component: () => import('@/views/system/OpenAppDetailView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
            {
              path: 'ai-box-apps',
              name: 'SystemAiBoxApps',
              component: () => import('@/views/system/AiBoxAppView.vue'),
              meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
            },
          ]
        },
        {
          path: 'projects',
          name: 'Projects',
          component: () => import('@/views/system/ProjectView.vue'),
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
        },
        {
          path: 'notification-rules',
          name: 'NotificationRules',
          component: () => import('@/views/system/NotificationRuleView.vue'),
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
        },
        {
          path: 'dingtalk-apps',
          name: 'DingTalkApps',
          component: () => import('@/views/system/DingTalkAppView.vue'),
          meta: { roles: ['platform_admin', 'tenant_admin', 'project_admin'] }
        }
      ]
    },
    {
      path: '/system/projects',
      redirect: '/projects'
    },
    {
      path: '/system/notification-rules',
      redirect: '/notification-rules'
    },
    {
      path: '/system/dingtalk-apps',
      redirect: '/dingtalk-apps'
    },
    {
      path: '/devices/video-linkage',
      redirect: '/video-linkage'
    },
    {
      path: '/devices/components',
      redirect: '/components'
    }
  ]
})

const MOBILE_PUBLIC_PATHS = [
  '/mobile/dingtalk/auth',
  '/mobile/dingtalk/bind',
  '/mobile/dingtalk/auto-bind',
  '/mobile/work-order'
]

function isMobilePublicPath(path: string): boolean {
  return MOBILE_PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + '/'))
}

router.beforeEach((to, from, next) => {
  const auth = useAuthStore()

  // 主动校验 token 是否过期，过期则清退到登录页
  const hasValidSession = auth.validateSession()

  // 移动端非公开页面需要登录；未登录时引导到钉钉授权页，保留 appId/nonce/id 等参数
  if (to.path.startsWith('/mobile') && !to.meta.public && !isMobilePublicPath(to.path) && !hasValidSession) {
    const query: Record<string, string | undefined> = {}
    if (to.query.appId) query.appId = to.query.appId as string
    if (to.query.nonce) query.nonce = to.query.nonce as string
    if (to.query.id) query.id = to.query.id as string
    next({ path: '/mobile/dingtalk/auth', query })
    return
  }

  if (!to.meta.public && !hasValidSession) {
    toast.error('登录已过期，请重新登录')
    next('/login')
    return
  }
  if (to.path === '/login' && hasValidSession) {
    next('/')
    return
  }
  const requiredRoles = (to.meta.roles as string[] | undefined) ?? []
  if (!to.meta.public && requiredRoles.length > 0 && !auth.hasAnyRole(requiredRoles)) {
    next('/dashboard')
    return
  }
  next()
})

// 登录跳转期间的全屏 loading：到达非登录目标页后关闭（覆盖了目标页 chunk 懒加载这段空白）
router.afterEach((to) => {
  if (to.path !== '/login') {
    // 动态导入避免循环依赖
    import('@/composables/useAppLoading').then(({ setAppLoading }) => setAppLoading(false))
  }
})

export default router
